import type { Order, OrderStatus } from '@cracked-mug/shared';
import { elapsedUrgency, formatElapsed, formatOrderNumber, formatPrice } from '@cracked-mug/shared';
import { AlertTriangle, Check, MapPin, ShoppingBag } from 'lucide-react';
import { cn } from '../../lib/cn';

interface OrderTicketProps {
  order: Order;
  onAdvance: (order: Order, next: OrderStatus) => Promise<void>;
  onCancel: (order: Order) => Promise<void>;
  isPending?: boolean;
}

const URGENCY_BORDER: Record<'normal' | 'warning' | 'critical', string> = {
  normal: 'border-wca-black/[0.07]',
  warning: 'border-wca-gold',
  critical: 'border-wca-red',
};

export function OrderTicket({ order, onAdvance, onCancel, isPending = false }: OrderTicketProps) {
  const urgency = elapsedUrgency(order.placedAt);
  const elapsed = formatElapsed(order.placedAt);
  // Single-step workflow: every active order is one tap from done.
  const isDone = order.status === 'completed' || order.status === 'cancelled';
  const isDelivery = order.fulfillmentMethod === 'delivery';

  return (
    <article
      className={cn(
        'bg-wca-surface rounded-2xl shadow-card border-2 overflow-hidden transition-all duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-card-lg',
        URGENCY_BORDER[urgency],
      )}
    >
      <header className="flex items-baseline justify-between px-4 py-3 border-b border-wca-gray-light">
        <p className="font-display text-2xl tabular-nums">{formatOrderNumber(order.orderNumber)}</p>
        <p
          className={cn(
            'font-mono text-sm tabular-nums',
            urgency === 'normal' && 'text-wca-gray-mid',
            urgency === 'warning' && 'text-wca-gold',
            urgency === 'critical' && 'text-wca-red font-semibold',
          )}
        >
          {elapsed}
        </p>
      </header>

      {/* Big, unmissable fulfillment banner — what the barista needs to know
          before reading anything else: deliver it or leave it on the counter. */}
      <div
        className={cn(
          'flex items-center gap-2 px-4 py-2.5 text-white',
          isDelivery ? 'bg-wca-black' : 'bg-wca-gold',
        )}
      >
        {isDelivery ? (
          <MapPin size={18} aria-hidden="true" />
        ) : (
          <ShoppingBag size={18} aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] leading-tight opacity-90">
            {isDelivery ? 'Deliver to' : 'Pickup at counter'}
          </p>
          <p className="text-sm font-semibold leading-snug">
            {isDelivery ? `Room ${order.teacherRoom}` : order.teacherName}
          </p>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-wca-gray-light">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-medium text-sm leading-tight">{order.teacherName}</p>
          {isDelivery && (
            <p className="font-display text-lg font-semibold text-wca-black leading-none">
              Room {order.teacherRoom}
            </p>
          )}
        </div>
        {order.printedAt === null && (
          <p className="text-xs font-mono uppercase tracking-wider text-wca-red mt-1">
            Not printed
          </p>
        )}
      </div>

      {order.teacherAllergies.length > 0 && (
        <div className="flex items-start gap-2 bg-wca-red px-4 py-2.5 text-white">
          <AlertTriangle size={18} aria-hidden="true" className="shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] leading-tight opacity-90">
              Allergies
            </p>
            <p className="text-sm font-semibold leading-snug">{order.teacherAllergies}</p>
          </div>
        </div>
      )}

      <ul className="px-4 py-3 space-y-3">
        {order.items.map((item, idx) => (
          <li key={`${item.itemId}-${String(idx)}`} className="text-sm">
            <p className="font-medium">
              {item.quantity}× {item.name}
            </p>
            {item.selectedCustomizations.map((cust) =>
              cust.optionLabels.length > 0 ? (
                <p key={cust.groupId} className="text-xs text-wca-gray-mid pl-4">
                  {cust.groupName}: {cust.optionLabels.join(', ')}
                </p>
              ) : null,
            )}
            {item.specialInstructions.length > 0 && (
              <p className="text-xs italic text-wca-red pl-4 mt-0.5">
                &ldquo;{item.specialInstructions}&rdquo;
              </p>
            )}
          </li>
        ))}
      </ul>

      {order.specialInstructions.length > 0 && (
        <div className="px-4 py-2 border-t border-wca-gray-light bg-wca-red-light">
          <p className="text-xs font-medium text-wca-red-dark">
            Order note: <span className="italic">{order.specialInstructions}</span>
          </p>
        </div>
      )}

      <footer className="px-4 py-3 border-t border-wca-gray-light flex items-center justify-between gap-3 bg-wca-chalk/50">
        <div className="text-xs text-wca-black/70">
          <p className="font-mono">{formatPrice(order.totalCents)}</p>
          {order.paymentStatus === 'unpaid' && <p className="text-wca-red font-semibold">Unpaid</p>}
        </div>
        <div className="flex items-center gap-1.5">
          {!isDone && (
            <>
              <button
                type="button"
                onClick={() => void onCancel(order)}
                disabled={isPending}
                className="inline-flex min-h-[44px] items-center rounded-full px-3 text-xs font-medium text-wca-black/70 transition-colors hover:bg-wca-gray-light hover:text-wca-red disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void onAdvance(order, 'completed')}
                disabled={isPending}
                className="inline-flex items-center gap-1.5 bg-wca-red hover:bg-wca-red-dark disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-full shadow-[0_6px_16px_-6px_rgba(196,18,48,0.5)] hover:shadow-[0_9px_22px_-6px_rgba(196,18,48,0.6)] transition-all active:scale-[0.97] disabled:active:scale-100 disabled:shadow-none"
              >
                <Check size={15} aria-hidden="true" />
                Done
              </button>
            </>
          )}
        </div>
      </footer>
    </article>
  );
}
